import Autocomplete from "@mui/material/Autocomplete"
import Box from "@mui/material/Box"
import FormHelperText from "@mui/material/FormHelperText"
import InputLabel from "@mui/material/InputLabel"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import { useId, useRef, useState } from "react"
import { Controller, type Control, type FieldValues } from "react-hook-form"

import { TextInputProps } from "@components/text_input/sub_components/primary_input"
import { INPUT_LABEL_BLUR, PRIMARY } from "@constants/colors"
import useTranslate from "@hooks/use_translate"
import { toArray } from "@utils/functions/to_array"

import styles from "./styles"

type Option = {
  label: string
  value: string | number | boolean
  color?: string
  icon?: string
  group?: string
}

export default function AutoCompleteInput<T extends FieldValues, K = unknown>(
  props: TextInputProps<T, K>
) {
  const [isFocus, setIsFocus] = useState(false)
  const componentId = useId()
  const { translate } = useTranslate()
  const shownOption = useRef<Option | undefined>(undefined)

  function onFocus(event: React.FocusEvent<HTMLInputElement>) {
    setIsFocus(true)
    props?.onFocus && props?.onFocus(event)
  }

  function onBlur(event: React.FocusEvent<HTMLInputElement>) {
    setIsFocus(false)
    props?.onBlur && props?.onBlur(event)
  }

  return (
    <Box sx={styles.container}>
      <Controller<FieldValues>
        name={props.name}
        control={props.control as unknown as Control<FieldValues>}
        render={({ field: { onChange, value }, fieldState: { error } }) => {
          const found = (toArray(props.selectOptions) as Option[]).find(
            (option) => option.value === value
          )
          // MUI wipes the typed text whenever `value` changes identity; callers often rebuild options per render.
          const previous = shownOption.current
          const selectedOption =
            found &&
            previous &&
            previous.value === found.value &&
            previous.label === found.label
              ? previous
              : found
          shownOption.current = selectedOption

          return (
            <>
              <InputLabel
                sx={{
                  ...styles.labelContainer,
                  color: isFocus ? PRIMARY : INPUT_LABEL_BLUR
                }}
                error={!!error}
                htmlFor={componentId}
                required={props?.required}
              >
                <Typography>{props.label}</Typography>
              </InputLabel>

              <Autocomplete<{
                label: string
                value: number | string | boolean
                color?: string
                icon?: string
                group?: string
              }>
                options={
                  toArray(props.selectOptions) as {
                    label: string
                    value: string | number | boolean
                    color?: string
                    icon?: string
                    group?: string
                  }[]
                }
                loading={props.loading}
                disabled={props.disabled}
                disableClearable={true as unknown as false}
                noOptionsText={translate("no_options")}
                groupBy={
                  props.groupBy ? (option) => option.group || "" : undefined
                }
                getOptionLabel={(option) => option.label}
                filterOptions={(options, state) => {
                  const inputValue = state.inputValue.toLowerCase()
                  return options.filter((option) => {
                    const labelMatch = option.label
                      .toLowerCase()
                      .includes(inputValue)
                    const groupMatch = option.group
                      ?.toLowerCase()
                      .includes(inputValue)
                    return labelMatch || groupMatch
                  })
                }}
                renderOption={(props, option) => (
                  <li {...props} style={{ color: option.color }}>
                    {option.icon && (
                      <span style={{ marginRight: "8px" }}>{option.icon}</span>
                    )}
                    {option.label}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={props.placeholder}
                    inputProps={{
                      ...params.inputProps,
                      style: { color: selectedOption?.color }
                    }}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: selectedOption?.icon ? (
                        <span style={{ marginRight: "4px", marginLeft: "8px" }}>
                          {selectedOption.icon}
                        </span>
                      ) : (
                        params.InputProps.startAdornment
                      )
                    }}
                  />
                )}
                onChange={(_, selectedOption) => {
                  onChange({ target: { value: selectedOption?.value } })
                }}
                onInputChange={props.onInputChange}
                value={selectedOption}
                onFocus={onFocus}
                onBlur={onBlur}
                id={componentId}
                sx={{
                  "& .MuiOutlinedInput-root.MuiInputBase-sizeSmall": {
                    pt: 0,
                    pl: 0,
                    pb: 0
                  },
                  " input": {
                    padding: "2px 4px!important"
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: error ? "#d32f2f" : undefined
                  },
                  ".MuiSelect-select caption": {
                    color: "text.disabled"
                  },
                  ...props?.sx
                }}
              />
              <FormHelperText
                error={!!error}
                sx={{ display: !!error ? "block" : "none" }}
              >
                {error ? error?.message : <></>}
              </FormHelperText>
            </>
          )
        }}
      />
    </Box>
  )
}
